import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  trans,
  useSoyuzExperiment,
  useSoyuzLocales
} from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import Homecard from '@spotahome/marketplace-common/src/components/Homecard';
import CurrencyFormatter from '@spotahome/marketplace-common/src/utils/CurrencyFormatter';

import { useIsUserLogged } from '@spotahome/ui-library';
import { getDateAsUTC } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import { useEcommerceManagerFactory } from '@spotahome/ecommerce-tracking';

import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';

import { useListingsAround } from './utils/useListingsAround';
import HomecardScrollList from './HomecardScrollList';

import './ListingsAround.scss';

const getCurrency = homecard =>
  new CurrencyFormatter(
    homecard.currencyLabel,
    homecard.currencySymbol,
    homecard.currencyIsoCode
  );

const ListingsAround = ({ moveIn, moveOut, listingId }) => {
  const { current: currentLocale } = useSoyuzLocales();
  const ecommerceManager = useEcommerceManagerFactory();
  const isUserLogged = useIsUserLogged();
  const listingsAround = useListingsAround(listingId, moveIn, moveOut);
  const ListingSimplificationBottomExperiment = useSoyuzExperiment(
    'ListingSimplificationBottom'
  );

  const currentYear = getDateAsUTC(new Date()).getFullYear();

  const handleHomecardClick = (homecard, position) => () => {
    try {
      ecommerceManager.trackSimilarPropertyClick(
        homecard,
        isUserLogged,
        currentLocale,
        position
      );
    } catch (err) {
      console.error('Failed to handleHomecardClick at additionalListings', {
        err
      });
    }

    SoyuzAnalytics.sendGA4Event('homecard-similar-properties', {
      action: 'click',
      section: 'listing-page'
    });
  };

  if (!listingsAround.length) return null;

  const className = classNames('listings-around', {
    'listings-around--variant':
      ListingSimplificationBottomExperiment.get('behaviour') === 'variant'
  });

  const titleClassName = classNames('listings-around__title', {
    'listings-around__title--left':
      ListingSimplificationBottomExperiment.get('behaviour') === 'variant'
  });

  return (
    <div className={className}>
      <div className={titleClassName}>
        <ListingTitle>{trans('listing.properties_around.title')}</ListingTitle>
      </div>
      <div className="listings-around__wrapper">
        <HomecardScrollList
          homecards={listingsAround}
          listClassName="listings-around__list"
          renderItem={(homecard, position, handleItemClick) => {
            const firstAvailableDate = getDateAsUTC(
              new Date(homecard.firstAvailableDate)
            );
            const firstAvailableYear = firstAvailableDate?.getFullYear();
            return (
              <Homecard
                key={homecard.id}
                isShowStatus
                data={homecard}
                position={position}
                currency={getCurrency(homecard)}
                moveIn={moveIn}
                moveOut={moveOut}
                tracking="ga-detail-properties-around-card"
                onClick={() => {
                  handleItemClick();
                  handleHomecardClick(data, position);
                }}
                hasLazyLoad
                showAvailableFromYear={firstAvailableYear !== currentYear}
                withTransparentBackground
                pageType="product_page"
                section="similar_properties"
              />
            );
          }}
        />
      </div>
    </div>
  );
};

ListingsAround.propTypes = {
  listingId: PropTypes.string.isRequired,
  moveIn: PropTypes.string.isRequired,
  moveOut: PropTypes.string.isRequired
};
export default ListingsAround;
